import { useCallback, useState } from "react";

import { useHistory } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";
import { Button, Stack, Typography } from "@mui/material";

import { useLocalStorage } from "../../../Components/hooks/storageHooks";
import { useErrorSnackbar } from "../../../Components/SharedSnackbar/SharedSnackbar.context";
import { useAssetsContext } from "../../../Context/customer/AssetContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { useIsFeatureEntitled } from "../../../Context/TierProvider";
import useCreateTicket from "../../../Pages/MPA/hooks/useCreateTicket";

export const EnableRealTime = () => {
  const history = useHistory();
  const { customer } = useCustomerContext();
  const [loading, setLoading] = useState(false);
  const { hasAWSAssets, hasGCPAssets } = useAssetsContext();
  const { createTicket } = useCreateTicket();
  const isEntitled = useIsFeatureEntitled("governance:costAnomalies:realTime");
  const [hasRegisteredInterest, setHasRegisteredInterest] = useLocalStorage<boolean>("enable-real-time", false);
  const showError = useErrorSnackbar();

  const handleRegisterInterest = useCallback(async () => {
    setLoading(true);
    try {
      await createTicket(customer.id, {
        subject: "Register interest for real-time cost anomalies",
        description:
          "Customer is interested in early access to real-time cost anomalies. Please reach out with more information.",
        priority: "normal",
        platformField: "doit",
        tags: ["upsell", "register-interest", "real-time-anomalies"],
      });
      setHasRegisteredInterest(true);
    } catch (error) {
      showError("An error occurred while sending the registration request.");
    }
    setLoading(false);
  }, [customer, createTicket, setHasRegisteredInterest, showError]);

  const handleEnableRealTime = () => {
    if (isEntitled) {
      const url = !hasAWSAssets && hasGCPAssets ? "./integrate/gcp" : "./integrate/aws";
      history.push(url);
    } else {
      handleRegisterInterest();
    }
  };

  if (hasRegisteredInterest) {
    return (
      <Stack direction="row" alignSelf="center" spacing={1} sx={{ mt: 1 }}>
        <CheckIcon sx={{ color: "success.main", fontSize: 16 }} data-cy="real-time-anomalies-success-icon" />

        <Typography variant="caption" color="success.main" data-cy="real-time-anomalies-success-msg">
          Thanks for your upgrade request. Your Account Manager will reach out to you shortly.
        </Typography>
      </Stack>
    );
  }

  return (
    <Button variant="outlined" loading={loading} onClick={handleEnableRealTime}>
      {isEntitled ? "Enable real-time cost anomalies" : "Upgrade to enable real-time anomalies"}
    </Button>
  );
};
